/* eslint-disable no-empty */
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { IMaskInput } from "react-imask";

/* ================== constants ================== */
const ANNUAL_INCOMES = [
  { id: 1, name: "$12,000-$49,999" },
  { id: 2, name: "$50,000-$99,999" },
  { id: 3, name: "$100,000-$249,999" },
  { id: 4, name: "$250,000-$499,999" },
  { id: 5, name: "$500,000+" },
];

const FIELD_CONFIG = [
  { type: "text", name: "firstname", label: "First Name *" },
  { type: "text", name: "lastname",  label: "Last Name *" },
  { type: "email",name: "email",     label: "Email Address *" },
  { type: "tel",  name: "phone",     label: "Phone Number *", mask: "(000) 000-0000" },
  { type: "text", name: "zip",       label: "Zip Code *",     mask: "00000000" },
];

const PHONE_REGEX = /^\(\d{3}\) \d{3}-\d{4}$/;
const ZIP_REGEX   = /^(\d{5}|\d{8})$/;

/* ================== utils ================== */
// Hosts that actually serve /.netlify/functions/api-proxy. Optimizers like
// NitroPack rehost the widget JS on the WordPress origin while keeping the
// original tools host inside the rewritten path, so match a known host
// anywhere in the src rather than trusting the URL's origin.
const TOOLS_HOSTS = ["tools.unclekam.com", "tools-unclekam.netlify.app"];

function toolsOriginFromSrc(src) {
  if (!src) return "";
  try {
    const u = new URL(src, location.href);
    if (TOOLS_HOSTS.includes(u.hostname)) return u.origin;
  } catch {}
  for (const host of TOOLS_HOSTS) {
    if (src.includes(`/${host}/`) || src.includes(`//${host}/`)) {
      return `https://${host}`;
    }
  }
  return "";
}

function detectToolsOrigin() {
  const fromCur = toolsOriginFromSrc(document.currentScript && document.currentScript.src);
  if (fromCur) return fromCur;
  const scripts = Array.from(document.getElementsByTagName("script"));
  for (let i = scripts.length - 1; i >= 0; i--) {
    const fromScan = toolsOriginFromSrc(scripts[i].src || "");
    if (fromScan) return fromScan;
  }
  const isQA = new URLSearchParams(location.search).has("qa_mode");
  return isQA ? "https://tools-unclekam.netlify.app" : "https://tools.unclekam.com";
}

/* ================== main ================== */
export default function MultiStepLeadForm({
  theme = "light",
  qa_mode,
  data_source = "VSL",
  redirectUrl,
  pro_id,
  proxyUrl,
}) {
  const css = `
    .ukf-success-check{font-size:2.75rem;line-height:1}
    .ukf-pointer{cursor:pointer}
    .ukf-muted-link{text-decoration:underline}
    .ukf-section-title{font-weight:600;margin-top:18px;margin-bottom:10px}
  `;

  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const qa = useMemo(() => (typeof qa_mode === "boolean" ? qa_mode : params.has("qa_mode")), [qa_mode, params]);
  const isDark = theme === "dark";

  // Robust proxy URL
  const PROXY_URL =
    proxyUrl ||
    window.__UK_PROXY_URL__ ||
    `${detectToolsOrigin()}/.netlify/functions/api-proxy`;

  const [formData, setFormData] = useState(() => ({
    firstname: params.get("firstname") || "",
    lastname:  params.get("lastname")  || "",
    email:     params.get("email")     || "",
    phone:     params.get("phone")     || "",
    zip:       params.get("zip")       || "",
    options:   [],
    income:    "",
    agreementAccepted: false,
  }));

  const setField = (name, value) =>
    setFormData((s) => (s[name] === value ? s : { ...s, [name]: value }));

  const [services, setServices] = useState([]);
  const [servicesLoading, setServicesLoading] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // success alert visibility
  const [showSuccess, setShowSuccess] = useState(false);

  const proId = params.get("pro_id");

  // Fetch services on mount
  useEffect(() => {
    let active = true;
    (async () => {
      setServicesLoading(true);
      try {
        const { data } = await axios.post(
          PROXY_URL,
          { path: "/get-services", qa_mode: qa, method: "GET" },
          { headers: { "Content-Type": "application/json" } }
        );
        if (active) setServices(Array.isArray(data) ? data : []);
      } catch {
        if (active) setServices([]);
      } finally {
        if (active) setServicesLoading(false);
      }
    })();
    return () => { active = false; };
  }, [PROXY_URL, qa]);

  // Validation (same rules as the multi-step flow)
  const phoneComplete = PHONE_REGEX.test(formData.phone);
  const zipComplete   = ZIP_REGEX.test(formData.zip);
  const step1AllFilled = ["firstname","lastname","email","phone","zip"]
    .every((n) => String(formData[n] || "").trim() !== "");
  const step1Valid = step1AllFilled && phoneComplete && zipComplete;
  const step2Valid = (formData.options || []).length > 0;
  const step3Valid = !!formData.income && !!formData.agreementAccepted;
  const allValid   = step1Valid && step2Valid && step3Valid;

  const handleSubmit = async () => {
    setSubmitError("");
    setShowSuccess(false);

    if (!allValid || submitting) {
      setSubmitError("Please complete all required fields.");
      return;
    }
    setSubmitting(true);

    const lead = {
      ...formData,
      phone: (formData.phone || "").replace(/[ ()-]/g, ""),
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };

    try {
      try {
        const ipRes = await axios.get("https://api.ipify.org?format=json");
        lead.clientIp = ipRes?.data?.ip || null;
      } catch { lead.clientIp = null; }

      await axios.post(
        PROXY_URL,
        {
          path: "/generate-lead",
          qa_mode: qa,
          data: lead,
          params: { tag: data_source || "VSL", pro_id: proId || pro_id }
        },
        { headers: { "Content-Type": "application/json" } }
      );

      // success: show alert
      setShowSuccess(true);

     if (redirectUrl) {
  const delayMs = 1000;
  const email = formData?.email;

  setTimeout(() => {
    // Build target URL (preserves any query already on redirectUrl)
    const url = new URL(redirectUrl, window.location.href);

    // Merge in ALL existing query params from the current page
    // (don’t overwrite anything already present on redirectUrl)
    const srcParams = new URLSearchParams(window.location.search);
    srcParams.forEach((value, key) => {
      if (!url.searchParams.has(key)) {
        url.searchParams.set(key, value);
      }
    });

    // Finally, inject/override the email param
    if (email) url.searchParams.set("email", email);

    window.location.assign(url.toString());
  }, delayMs);
} else {
        // hide success after 3s when no redirect target
        setTimeout(() => setShowSuccess(false), 3000);
      }
    } catch (err) {
      const msg = err?.response?.data?.message || "Server error. Please try again later.";
      setSubmitError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={`container px-3 px-md-4 py-3 ${isDark ? "" : ""}`}>
      <style>{css}</style>
      <div className="mx-auto" style={{ maxWidth: 860 }}>
        <form onSubmit={(e) => e.preventDefault()}>
          {/* ---------- Contact Section ---------- */}
          <div className="row g-3">
            {FIELD_CONFIG.map((cfg) => {
              const colClass = cfg.name === "firstname" || cfg.name === "lastname" ? "col-md-6" : "col-md-6";
              const val = formData[cfg.name] || "";
              const invalid =
                (cfg.name === "phone" && val && !phoneComplete) ||
                (cfg.name === "zip"   && val && !zipComplete);

              return (
                <div className={colClass} key={cfg.name}>
                  {cfg.mask ? (
                    <IMaskInput
                      id={cfg.name}
                      name={cfg.name}
                      className={`form-control ${invalid ? "is-invalid" : ""}`}
                      placeholder={cfg.label}
                      mask={cfg.mask}
                      lazy={true}
                      overwrite={true}
                      defaultValue={val}
                      onAccept={(v) => { if (v !== formData[cfg.name]) setField(cfg.name, v); }}
                      type={cfg.type}
                      inputMode={cfg.name === "zip" ? "numeric" : "tel"}
                      autoComplete="off"
                    />
                  ) : (
                    <input
                      id={cfg.name}
                      name={cfg.name}
                      className={`form-control`}
                      placeholder={cfg.label}
                      type={cfg.type}
                      value={val}
                      onChange={(e) => setField(cfg.name, e.target.value)}
                      autoComplete="off"
                    />
                  )}
                  {invalid && (
                    <div className="invalid-feedback">
                      {cfg.name === "phone" ? "Use format (555) 555-5555" : "Invalid ZIP"}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* ---------- Divider ---------- */}
          <hr className="mt-4" />

          {/* ---------- Services + Income ---------- */}
          <div className="row">
            {/* Services (left) */}
            <div className="col-md-6">
              <div className={`ukf-section-title ${isDark ? "text-light" : "text-dark"}`}>
                What can we help you with?
              </div>
              {servicesLoading && <div className="text-muted mb-2">Loading services…</div>}
              {!servicesLoading && services.length === 0 && <div className="text-muted mb-2">No services found.</div>}
              <div className="d-flex flex-column gap-2">
                {services.map((s) => {
                  const checked = formData.options.includes(String(s.id));
                  return (
                    <label key={s.id} className={`${isDark ? "text-light" : "text-dark"} ukf-pointer`}>
                      <input
                        type="checkbox"
                        className="form-check-input me-2"
                        checked={checked}
                        onChange={(e) => {
                          const id = String(s.id);
                          setFormData((prev) => {
                            const next = new Set(prev.options.map(String));
                            if (e.target.checked) next.add(id); else next.delete(id);
                            return { ...prev, options: Array.from(next) };
                          });
                        }}
                      />
                      {s.name}
                    </label>
                  );
                })}
              </div>
              {!step2Valid && (
                <div className="text-danger mt-2 small">Please select at least one option.</div>
              )}
            </div>

            {/* Income (right) */}
            <div className="col-md-6">
              <div className={`ukf-section-title ${isDark ? "text-light" : "text-dark"}`}>
                What is your annual income?
              </div>
              <div className="d-flex flex-column gap-2">
                {ANNUAL_INCOMES.map((inc) => (
                  <label key={inc.id} className={`${isDark ? "text-light" : "text-dark"} ukf-pointer`}>
                    <input
                      type="radio"
                      name="annual_income"
                      className="form-check-input me-2"
                      checked={String(formData.income) === String(inc.id)}
                      value={inc.id}
                      onChange={(e) => setField("income", e.target.value)}
                    />
                    {inc.name}
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* ---------- Agreement ---------- */}
          <div className="mt-3">
            <label htmlFor="ukf_user_agreement" className={`${isDark ? "text-light" : "text-dark"} ukf-pointer`}>
              <input
                id="ukf_user_agreement"
                type="checkbox"
                className="form-check-input me-2"
                checked={!!formData.agreementAccepted}
                onChange={(e) => setField("agreementAccepted", e.target.checked)}
              />
              <span>
                I agree to the{" "}
                <a className={`ukf-muted-link ${isDark ? "text-light" : ""}`} href="https://app.unclekam.com/user-agreement" target="_blank" rel="noreferrer">Uncle Kam Terms of Service</a>{" "}
                and{" "}
                <a className={`ukf-muted-link ${isDark ? "text-light" : ""}`} href="https://app.unclekam.com/privacy-policy" target="_blank" rel="noreferrer">Privacy Policy</a>{" "}
                and agree to receive emails and texts about promotions at the phone number and email provided, and understand this consent is not required to purchase. By checking this box you agree to receive text messages from; Reply STOP to opt out; Reply HELP for help; Message frequency varies; Message and data rates may apply.
              </span>
            </label>
          </div>

          {/* ---------- Alerts ---------- */}
          {!!submitError && (
            <div className="alert alert-danger mt-3" role="alert">{submitError}</div>
          )}
          {showSuccess && (
            <div className="alert alert-success mt-3" role="alert">Data added successfully!</div>
          )}

          {/* ---------- Submit ---------- */}
          <div className="mt-3">
            <button
              type="button"
              className="btn btn-primary px-4"
              disabled={submitting}
              onClick={handleSubmit}
            >
              {submitting ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                  Submitting…
                </>
              ) : (
                "Get started today"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
